'use client'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost' | 'ai'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  accent?: boolean
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'var(--accent)',
    backgroundImage: 'var(--btn-accent-sheen, none)',
    color: 'white',
    border: 'none',
    boxShadow: 'var(--btn-accent-shadow, none)',
  },
  secondary: { background: 'var(--surface-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' },
  ghost: { background: 'transparent', color: 'var(--text-secondary)', border: 'none' },
  danger: { background: 'var(--fg-danger)', color: 'white', border: 'none' },
  'danger-ghost': { background: 'transparent', color: 'var(--fg-danger)', border: 'none' },
  ai: { background: 'var(--accent-muted)', color: 'var(--accent-emphasis)', border: '1px solid var(--border-accent)' },
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px]',
  md: 'h-8 px-3.5 text-[13px]',
  lg: 'h-9 px-4 text-[13px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', accent, className, style, children, ...props }, ref) => {
    const baseStyle = { ...variantStyles[variant] }
    if (accent && variant === 'ghost') baseStyle.color = 'var(--accent)'

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-all duration-150 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none',
          sizeClasses[size],
          className,
        )}
        style={{ ...baseStyle, ...style }}
        {...props}
      >
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'sm' | 'md'
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'md', className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-lg transition-all duration-100 active:scale-95',
          size === 'sm' ? 'w-7 h-7' : 'w-8 h-8',
          className,
        )}
        style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.borderColor = 'var(--border-emphasis)' }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--border-default)' }}
        {...props}
      >
        {children}
      </button>
    )
  },
)
IconButton.displayName = 'IconButton'
